import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useTextUnderline } from '../hooks/useTextUnderline';
import { client, urlFor } from '../sanityClient';
import { PortableText } from '@portabletext/react';

// â”€â”€ Sanity query â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const QUERY = `*[_type == "post"] | order(publishedAt desc) {
  _id,
  title,
  slug,
  category,
  excerpt,
  author,
  publishedAt,
  readTime,
  coverImage,
  body
}`;

// â”€â”€ Category colors â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const CATEGORY_STYLES = {
  'Insights':      { background: 'rgba(113,62,165,0.1)', color: 'var(--primary-purple)' },
  'Education':     { background: 'rgba(225,165,54,0.12)', color: '#b8800a' },
  'Trust & Safety':{ background: 'rgba(83,124,122,0.12)', color: 'var(--success-green)' },
  'Developers':    { background: 'rgba(113,62,165,0.1)', color: 'var(--primary-purple)' },
  'Company News':  { background: 'rgba(225,165,54,0.12)', color: '#b8800a' },
};

const COVER_GRADIENTS = {
  'Insights':      'linear-gradient(135deg, #1a0a2e 0%, #713ea5 60%, #9259d4 100%)',
  'Education':     'linear-gradient(135deg, #1a1200 0%, #c48a1e 60%, #e1a536 100%)',
  'Trust & Safety':'linear-gradient(135deg, #0a1a1a 0%, #3d6260 60%, #537c7a 100%)',
  'Developers':    'linear-gradient(135deg, #0e0a1a 0%, #2d1b4e 60%, #713ea5 100%)',
  'Company News':  'linear-gradient(135deg, #0f0a00 0%, #b8800a 60%, #e1a536 100%)',
};

// â”€â”€ Portable Text components (rich text renderer) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ptComponents = {
  types: {
    image: ({ value }) => (
      <figure className="blog-article-image">
        <img
          src={urlFor(value).width(800).url()}
          alt={value.caption || ''}
          style={{ width: '100%', borderRadius: '12px', margin: '1.5rem 0' }}
        />
        {value.caption && (
          <figcaption style={{ textAlign: 'center', fontSize: '0.82rem', color: '#94a3b8', marginTop: '0.5rem' }}>
            {value.caption}
          </figcaption>
        )}
      </figure>
    ),
  },
  block: {
    h2: ({ children }) => <h2>{children}</h2>,
    h3: ({ children }) => <h3>{children}</h3>,
    normal: ({ children }) => <p>{children}</p>,
    blockquote: ({ children }) => (
      <blockquote style={{
        borderLeft: '3px solid var(--primary-purple)',
        paddingLeft: '1.25rem',
        margin: '1.5rem 0',
        color: '#64748b',
        fontStyle: 'italic',
      }}>
        {children}
      </blockquote>
    ),
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    code: ({ children }) => (
      <code style={{
        background: '#f1f5f9',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.875em',
        fontFamily: 'Courier New, monospace',
      }}>
        {children}
      </code>
    ),
    link: ({ value, children }) => (
      <a href={value?.href} target="_blank" rel="noopener noreferrer">{children}</a>
    ),
  },
};

// â”€â”€ Format date â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

// â”€â”€ Cover placeholder pattern â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function CoverPattern({ category }) {
  if (category === 'Education' || category === 'Company News') return (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0 }}>
      <defs>
        <pattern id="blog-grid" width="28" height="28" patternUnits="userSpaceOnUse">
          <path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="0.5"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#blog-grid)"/>
    </svg>
  );
  if (category === 'Trust & Safety') return (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0 }}>
      <defs>
        <pattern id="blog-dots" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.5" fill="rgba(255,255,255,0.09)"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#blog-dots)"/>
    </svg>
  );
  if (category === 'Developers') return (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0 }}>
      {[...Array(8)].map((_, i) => (
        <line key={i} x1={`${i * 15}%`} y1="0" x2={`${i * 15 + 30}%`} y2="100%"
          stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
      ))}
    </svg>
  );
  // Insights (default)
  return (
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0 }}>
      <circle cx="85%" cy="30%" r="90" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5"/>
      <circle cx="85%" cy="30%" r="140" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1.5"/>
      <circle cx="15%" cy="75%" r="65" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5"/>
    </svg>
  );
}

// â”€â”€ Blog card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function BlogCard({ post, onClick, featured = false }) {
  const catStyle = CATEGORY_STYLES[post.category] || CATEGORY_STYLES['Insights'];
  const gradient = COVER_GRADIENTS[post.category] || COVER_GRADIENTS['Insights'];

  return (
    <article
      className={`blog-card${featured ? ' blog-card--featured' : ''}`}
      onClick={() => onClick(post)}
    >
      {/* Cover */}
      <div className="blog-card-cover" style={{ background: gradient }}>
        <CoverPattern category={post.category} />
        {post.coverImage && (
          <img
            src={urlFor(post.coverImage).width(800).height(400).url()}
            alt={post.title}
            className="blog-card-cover-img"
          />
        )}
      </div>

      {/* Body */}
      <div className="blog-card-body">
        <div className="blog-card-top">
          <span className="blog-category" style={catStyle}>{post.category}</span>
          <span className="blog-read-time">{post.readTime}</span>
        </div>
        <h2 className="blog-card-title">{post.title}</h2>
        <p className="blog-card-excerpt">{post.excerpt}</p>
        <div className="blog-card-footer">
          <div className="blog-author">
            <div className="blog-author-avatar" style={{ background: 'linear-gradient(135deg, #713ea5, #9259d4)' }}>
              {post.author?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="blog-author-info">
              <span className="blog-author-name">{post.author}</span>
              <span className="blog-author-date">{formatDate(post.publishedAt)}</span>
            </div>
          </div>
          <button className="blog-read-btn">
            Read
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
}

// Article view 
function ReadNextCard({ post, onNavigate }) {
  if (!post) return null;
  const catStyle = CATEGORY_STYLES[post.category] || CATEGORY_STYLES['Insights'];
  const gradient = COVER_GRADIENTS[post.category] || COVER_GRADIENTS['Insights'];

  return (
    <button
      type="button"
      className="blog-read-next-card"
      onClick={() => onNavigate(post)}
      aria-label={`Read next post: ${post.title}`}
    >
      <div className="blog-read-next-cover" style={{ background: gradient }}>
        <CoverPattern category={post.category} />
        {post.coverImage && (
          <img
            src={urlFor(post.coverImage).width(800).height(420).url()}
            alt={post.title}
            className="blog-read-next-img"
          />
        )}
      </div>
      <div className="blog-read-next-meta">
        <span className="blog-read-next-category" style={catStyle}>{post.category}</span>
        <span className="blog-read-next-date">{formatDate(post.publishedAt)}</span>
      </div>
      <h3 className="blog-read-next-title">{post.title}</h3>
    </button>
  );
}

function ArticleView({ post, onBack, prevPost, nextPost, onNavigate }) {
  const catStyle = CATEGORY_STYLES[post.category] || CATEGORY_STYLES['Insights'];
  const readNextPost = prevPost || nextPost;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || 'Chattak Blog';
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);

  const handleInstagramShare = async () => {
    try {
      if (shareUrl) {
        await navigator.clipboard.writeText(shareUrl);
      }
    } catch (err) {
      // Ignore clipboard errors (permissions / unsupported)
    }
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="blog-article-wrap">
      <div className="container-medium">
        <button className="blog-back-btn" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Blog
        </button>

        <div className="blog-article-layout">
          <article className="blog-article-main">
            {/* Cover image in article */}
            {post.coverImage && (
              <div className="blog-article-cover">
                <img
                  src={urlFor(post.coverImage).width(1200).height(500).url()}
                  alt={post.title}
                  style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: '500px' }}
                />
              </div>
            )}
            <header className="blog-article-header">
              <span className="blog-category" style={catStyle}>{post.category}</span>
              <h1 className="blog-article-title">{post.title}</h1>
              <div className="blog-article-meta">
                <div className="blog-author">
                  <div className="blog-author-avatar" style={{ background: 'linear-gradient(135deg, #713ea5, #9259d4)', width: 44, height: 44, fontSize: '0.82rem' }}>
                    {post.author?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="blog-author-info">
                    <span className="blog-author-name">{post.author}</span>
                    <span className="blog-author-date">{formatDate(post.publishedAt)} · {post.readTime}</span>
                  </div>
                </div>
              </div>
            </header>



            <div className="blog-article-body">
              <PortableText value={post.body} components={ptComponents} />
            </div>

            <div className="blog-share">
              <span className="blog-share-label">Share</span>
              <div className="blog-share-buttons">
                <a
                  className="blog-share-btn"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Facebook"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M13 9h3V6h-3c-2.206 0-4 1.794-4 4v2H7v3h2v6h3v-6h3l1-3h-4v-2c0-.551.449-1 1-1z"
                    />
                  </svg>
                </a>
                <a
                  className="blog-share-btn"
                  href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on X"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M18.9 3H21l-6.5 7.4L22 21h-6.2l-4.6-6-5.3 6H3l7-8L2 3h6.3l4.2 5.4L18.9 3zm-1.1 16h1.9L7.2 5H5.2l12.6 14z"
                    />
                  </svg>
                </a>
                <a
                  className="blog-share-btn"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on LinkedIn"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 8.98h3.96V21H3V8.98zM9.5 8.98h3.8v1.64h.05c.53-1 1.83-2.05 3.76-2.05 4.02 0 4.76 2.65 4.76 6.1V21H18V15.2c0-1.38-.03-3.16-1.93-3.16-1.93 0-2.22 1.5-2.22 3.06V21H9.5V8.98z"
                    />
                  </svg>
                </a>
                <button
                  type="button"
                  className="blog-share-btn"
                  onClick={handleInstagramShare}
                  aria-label="Share on Instagram"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm10 2H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm-5 3.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 0 1 12 8.5zm0 2A2.5 2.5 0 1 0 14.5 12 2.5 2.5 0 0 0 12 10.5zm4.75-3.75a1 1 0 1 1-1 1 1 1 0 0 1 1-1z"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <button className="blog-back-btn blog-back-btn--bottom" onClick={onBack}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back to Blog
            </button>
          </article>

          <aside className="blog-article-nav blog-article-nav--next">
            {readNextPost && (
              <div className="blog-read-next-wrap">
                <div className="blog-read-next-heading">Read Next</div>
                <ReadNextCard post={readNextPost} onNavigate={onNavigate} />
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
// Main Blog Page 
export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePost, setActivePost] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  useTextUnderline();

  useEffect(() => {
    client.fetch(QUERY)
      .then(data => { setPosts(data); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  const categories = ['All', ...Array.from(new Set(posts.map(p => p.category)))];
  const filtered = activeCategory === 'All' ? posts : posts.filter(p => p.category === activeCategory);
  const handleSelectPost = (post) => {
    setActivePost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (activePost) {
    const navPosts = filtered.length ? filtered : posts;
    const currentIndex = navPosts.findIndex(p => p._id === activePost._id);
    const prevPost = currentIndex >= 0 ? navPosts[currentIndex + 1] : null;
    const nextPost = currentIndex > 0 ? navPosts[currentIndex - 1] : null;

    return (
      <>
        <Navbar />
        <ArticleView post={activePost} onBack={() => {
          setActivePost(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }} prevPost={prevPost} nextPost={nextPost} onNavigate={handleSelectPost} />
        <Footer />
      </>
    );
  }

  const [featured, ...rest] = filtered;

  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="policy-hero">
        <div className="policy-hero-inner">
          <div>
            <div className="policy-hero-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
              Chattak Blog
            </div>
            <h1>
              Insights, updates &amp; ideas from the{' '}
              <span className="js-text-underline" data-underline="sm">Chattak team</span>
            </h1>
            <p style={{ fontSize: '1rem', lineHeight: 1.8, color: 'var(--text-dark)', opacity: 0.85, maxWidth: 660 }}>
              Thoughts on digital payments, financial inclusion, and building fintech infrastructure for Afghanistan.
            </p>
          </div>
          <div className="policy-hero-visual">
            <div className="img-placeholder">
              <img
                src="/icons/blog_image.png"
                alt="Mobile-first digital finance illustration"
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filter tabs */}
      {!loading && posts.length > 0 && (
        <div className="blog-filters">
          <div className="container-medium">
            <div className="blog-filter-tabs">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`blog-filter-tab${activeCategory === cat ? ' active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Posts */}
      <div className="blog-content">
        <div className="container-medium">
          {loading ? (
            <div className="blog-loading">
              <div className="blog-loading-spinner" />
              <p>Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '4rem 0' }}>
              No posts published yet. Check back soon!
            </p>
          ) : filtered.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '4rem 0' }}>
              No posts in this category yet.
            </p>
          ) : (
            <>
              {featured && <BlogCard post={featured} onClick={handleSelectPost} featured />}
              {rest.length > 0 && (
                <div className="blog-grid">
                  {rest.map(post => (
                    <BlogCard key={post._id} post={post} onClick={handleSelectPost} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
}
